import { FC, Fragment, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { GamePhase } from '../../prize-box-page.types';
import { Transition } from '@headlessui/react';

import { MobileRoundAnimation } from 'modules/Room/features/mobile-round-animation/mobile-round-animation';

import { MovesEnum, RoundOutcome } from 'shared/constants/shared-enums';

interface IRoundAnimationProps {
  phase: GamePhase;
  pointBalance: number;
  snapshotPoints: number;
  amIPlayerA: boolean;
  playerA: { wins: number };
  playerB: { wins: number };
  lastMoves: { playerAMove: MovesEnum; playerBMove: MovesEnum };
  lastRoundOutcome: RoundOutcome;
  onRoundAnimationFinished?: () => void;
}

export const RoundAnimation: FC<IRoundAnimationProps> = ({
  phase,
  pointBalance,
  snapshotPoints,
  amIPlayerA,
  playerA,
  playerB,
  lastMoves,
  lastRoundOutcome,
  onRoundAnimationFinished,
}) => {
  const { t } = useTranslation();
  const doneRef = useRef(false);
  const [showRoundAnimation, setShowRoundAnimation] = useState(false);
  const prevShowRef = useRef(false);

  useEffect(() => {
    doneRef.current = false;
  }, [phase]);

  useEffect(() => {
    if (phase === GamePhase.Result && !doneRef.current) {
      setShowRoundAnimation(true);
    }
  }, [phase]);

  useEffect(() => {
    if (prevShowRef.current && !showRoundAnimation) {
      onRoundAnimationFinished?.();
    }
    prevShowRef.current = showRoundAnimation;
  }, [showRoundAnimation, onRoundAnimationFinished]);

  return createPortal(
    <Transition
      show={showRoundAnimation}
      as={Fragment}
      enter="transition-opacity duration-200"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="transition-opacity duration-150"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div className="grid-background fixed left-0 right-0 top-0 z-50">
        <MobileRoundAnimation
          pointBalance={Number(pointBalance)}
          prevPoints={Number(snapshotPoints)}
          score={{
            first: Number(amIPlayerA ? playerA.wins : playerB.wins),
            second: Number(amIPlayerA ? playerB.wins : playerA.wins),
          }}
          cards={{
            myMove: amIPlayerA ? lastMoves.playerAMove : lastMoves.playerBMove,
            opponentMove: amIPlayerA ? lastMoves.playerBMove : lastMoves.playerAMove,
          }}
          lastRoundOutcome={lastRoundOutcome}
          setShowRoundAnimation={setShowRoundAnimation}
          textForWin={t('prizeBox.roundAnimationTexts.win')}
          textForLose={t('prizeBox.roundAnimationTexts.lose')}
          textForDraw={t('prizeBox.roundAnimationTexts.draw')}
        />
      </div>
    </Transition>,
    document.body,
  );
};
